import { Sparkles, ArrowRight, ExternalLink } from 'lucide-react'
import { GRADE_COLORS } from '@/lib/radar/scoring'
import { sanitizeHref } from '@/lib/sanitize'
import type { ToolDetailProps } from './detailRegistry'
import { PartialCoverageBanner } from './PartialCoverageBanner'
import {
  responseCoverageForResult,
  type ResponseCoverage,
} from '@/lib/tools/response-coverage'

interface SourceInfluenceViewData {
  grade: string | null
  overallScore: number | null
  scoreStatus?: 'graded' | 'withheld'
  coverage?: ResponseCoverage
  url?: string
  aiSummary?: { summary?: string; actions?: string[] } | null
  topSources?: {
    domain: string
    context?: string
    citationCount: number
    providers?: string[]
  }[]
  gapAnalysis?: string
  /** Providers that returned usable responses (carried by the tool result). */
  providerCoverage?: string[]
  recommendations?: { priority: string; message: string }[]
}

/**
 * Dashboard detail renderer for `sourceInfluence` (Phase 3, Step 2 — Class D,
 * dashboard-only). Faithfully reproduces the legacy ToolDetailPanel rendering:
 * header (icon + name + grade pill + url), the AI Analysis block (when present),
 * the Top Sources rank list (rank badge + provider pills + gapAnalysis), and
 * recommendations.
 *
 * sourceInfluence has NO `scoreBreakdown`, so the breakdown section is fully
 * absent — no heading, no container. Grade comes from `data.grade` — never
 * recomputed. Render-layer only.
 */
export function SourceInfluenceDetail({
  data,
  toolIcon: Icon,
  toolColor,
  toolName,
}: ToolDetailProps) {
  const d = data as unknown as SourceInfluenceViewData
  const hasUsableScore =
    d.scoreStatus !== 'withheld' &&
    typeof d.overallScore === 'number' &&
    Number.isFinite(d.overallScore) &&
    typeof d.grade === 'string' &&
    d.grade in GRADE_COLORS
  const gradeColor = hasUsableScore
    ? GRADE_COLORS[d.grade as keyof typeof GRADE_COLORS]
    : 'var(--muted-foreground)'
  const responseCoverage = responseCoverageForResult(d)

  return (
    <div
      id='tool-detail-panel'
      className='rounded-xl border border-border bg-card p-5 space-y-5'
    >
      {/* Header */}
      <div className='flex items-baseline gap-3'>
        <span className='relative top-[3px] flex-shrink-0'>
          <Icon size={22} style={{ color: toolColor }} />
        </span>
        <h3 className='text-base font-bold text-foreground leading-none'>
          {toolName}
        </h3>
        <span
          className='text-sm font-bold px-2.5 py-1 rounded leading-none'
          style={{
            background: hasUsableScore ? `${gradeColor}15` : 'var(--muted)',
            color: gradeColor,
          }}
        >
          {hasUsableScore ? `${d.overallScore}/100` : 'Score withheld'}
        </span>
        {d.url &&
          (() => {
            const safeUrl = sanitizeHref(d.url, {
              allowedSchemes: ['https:', 'http:'],
            })
            if (!safeUrl) return null
            return (
              <a
                href={safeUrl}
                target='_blank'
                rel='noopener noreferrer'
                className='ml-auto flex items-center gap-1 text-xs hover:underline text-muted-foreground'
              >
                {d.url} <ExternalLink size={9} />
              </a>
            )
          })()}
      </div>

      {/* Partial provider coverage disclosure */}
      {responseCoverage ? (
        <PartialCoverageBanner coverage={responseCoverage} />
      ) : d.providerCoverage ? (
        <PartialCoverageBanner respondedProviders={d.providerCoverage} />
      ) : null}

      {/* AI Summary */}
      {d.aiSummary?.summary && (
        <div
          className='rounded-lg border p-5'
          style={{ borderColor: '#008dcc20', background: '#008dcc08' }}
        >
          <div className='flex items-center gap-2 mb-3'>
            <Sparkles size={14} style={{ color: '#008dcc' }} />
            <span
              className='text-xs font-bold uppercase tracking-wide'
              style={{ color: '#008dcc' }}
            >
              AI Analysis
            </span>
          </div>
          <p className='text-sm leading-relaxed text-foreground/90'>
            {d.aiSummary.summary}
          </p>
          {d.aiSummary.actions && d.aiSummary.actions.length > 0 && (
            <ul className='mt-4 space-y-2'>
              {d.aiSummary.actions.map((a, i) => (
                <li
                  key={i}
                  className='flex items-start gap-2 text-sm text-foreground/80'
                >
                  <ArrowRight
                    size={14}
                    className='mt-0.5 flex-shrink-0 text-primary'
                  />
                  {a}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Source Influence: top sources */}
      {d.topSources && (
        <div>
          <h4 className='text-xs font-bold mb-3 uppercase tracking-wide text-muted-foreground'>
            Top Sources Referenced in AI Answers ({d.topSources.length})
          </h4>
          <div className='space-y-2'>
            {d.topSources.map((src, i) => (
              <div
                key={src.domain}
                className='flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2.5'
              >
                <span className='w-7 h-7 rounded flex items-center justify-center text-sm font-black flex-shrink-0 bg-muted text-muted-foreground'>
                  {i + 1}
                </span>
                <div className='flex-1 min-w-0'>
                  <p className='text-sm font-semibold truncate text-foreground'>
                    {src.domain}
                  </p>
                  {src.context && (
                    <p className='text-xs truncate text-muted-foreground mt-0.5'>
                      {src.context}
                    </p>
                  )}
                </div>
                <div className='flex items-center gap-2 flex-shrink-0'>
                  <span className='text-sm font-mono font-bold text-foreground'>
                    {src.citationCount}x
                  </span>
                  <div className='flex gap-1'>
                    {src.providers?.map(p => (
                      <span
                        key={p}
                        className='text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground'
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {d.gapAnalysis && (
            <p className='text-sm mt-4 leading-relaxed text-foreground/80'>
              {d.gapAnalysis}
            </p>
          )}
        </div>
      )}

      {/* Recommendations */}
      {d.recommendations && d.recommendations.length > 0 && (
        <div>
          <h4 className='text-xs font-bold mb-3 uppercase tracking-wide text-muted-foreground'>
            Recommendations ({d.recommendations.length})
          </h4>
          <div className='space-y-2.5'>
            {d.recommendations.map((rec, i) => (
              <div
                key={i}
                className='flex items-start gap-3 text-sm leading-relaxed'
              >
                <span
                  className='w-2 h-2 rounded-full mt-1.5 flex-shrink-0'
                  style={{
                    background:
                      rec.priority === 'high'
                        ? '#F90B8A'
                        : rec.priority === 'medium'
                          ? 'var(--warning-text)'
                          : '#22c55e',
                  }}
                />
                <span className='text-foreground/85'>{rec.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
